/**
 * Utilidades para el manejo de cantidades decimales en el POS.
 *
 * - `MAX_DECIMAL_PLACES`:  cantidad máxima de decimales permitida (default 3).
 * - `roundQty`:            redondea una cantidad al número configurado de decimales
 *                          evitando errores de punto flotante.
 * - `safeMultiply`:        multiplica cantidad × precio con redondeo seguro.
 * - `formatQuantity`:      formatea la cantidad para visualización en el carrito.
 * - `isValidDecimalInput`: valida una cadena de texto ingresada por el usuario.
 * - `sanitizeDecimalInput`:convierte entrada parcial (p.ej. ".5") a "0.5".
 */

/** Máximo de decimales permitido para cantidades (configurable). */
export const MAX_DECIMAL_PLACES = 2;

/**
 * Redondea una cantidad al número de decimales indicado.
 * Usa aritmética entera internamente para evitar errores de punto flotante.
 */
export const roundQty = (
  value: number,
  decimals: number = MAX_DECIMAL_PLACES
): number => {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

/**
 * Multiplica `quantity × price` con redondeo a 2 decimales (moneda).
 * Evita artefactos como 1.1 * 2.2 = 2.4200000000000004.
 */
export const safeMultiply = (quantity: number, price: number): number => {
  return roundQty(quantity * price, 2);
};

/** Redondea un monto monetario a 2 decimales. */
export const roundMoney = (value: number): number => roundQty(value, 2);

/** Comparación segura de montos (evita errores de punto flotante). */
export const isMoneyEqual = (a: number, b: number): boolean =>
  roundMoney(a) === roundMoney(b);

/** Saldo pendiente de pago, nunca negativo ni residual por float. */
export const getRemainingPayment = (total: number, paid: number): number => {
  const remaining = roundMoney(total) - roundMoney(paid);
  return remaining > 0 ? remaining : 0;
};

/** Monto menor a medio céntimo (artefacto de float). */
export const isNegligibleAmount = (amount: number): boolean =>
  Math.abs(amount) < 0.005;

/**
 * Formatea la cantidad para mostrar en el carrito / badge.
 *
 * - Si es entero → "2"      (sin decimales).
 * - Si tiene decimales → "1.500" (siempre 3 decimales para consistencia visual).
 */
export const formatQuantity = (
  qty: number,
  decimals: number = MAX_DECIMAL_PLACES
): string => {
  return Number.isInteger(qty) ? qty.toString() : qty.toFixed(decimals);
};

/**
 * Valida que la cadena sea un número decimal válido dentro de los límites
 * del POS (máximo `maxDecimals` decimales).
 *
 * Acepta: "0", "12", "1.5", "0.123", ".5" (se convierte luego).
 * Rechaza: "1.5.5", "1.1234" (si maxDecimals=3), textos, negativos.
 */
export const isValidDecimalInput = (
  value: string,
  maxDecimals: number = MAX_DECIMAL_PLACES
): boolean => {
  if (value === "" || value === ".") return true; // estado intermedio válido
  const regex = new RegExp(`^\\d*(\\.\\d{0,${maxDecimals}})?$`);
  return regex.test(value);
};

/**
 * Sanitiza la entrada:
 * - "." → "0."
 * - vacío → "0"
 * - quita ceros a la izquierda innecesarios (excepto "0." y "0").
 */
export const sanitizeDecimalInput = (value: string): string => {
  if (value === "" || value === undefined || value === null) return "0";
  if (value === ".") return "0.";
  // Quitar ceros a la izquierda salvo "0." o "0"
  if (value.length > 1 && value.startsWith("0") && value[1] !== ".") {
    return value.replace(/^0+/, "") || "0";
  }
  return value;
};
